export interface BuildBrandVisibilityQueriesOptions {
  topic: string;
  audience?: string;
  count?: number;
}

const DEFAULT_QUERY_COUNT = 8;
const MIN_QUERY_COUNT = 1;
const MAX_QUERY_COUNT = 25;

const normalizeCount = (count?: number): number => {
  if (!Number.isFinite(count)) {
    return DEFAULT_QUERY_COUNT;
  }

  const truncatedCount = Math.trunc(count ?? DEFAULT_QUERY_COUNT);
  return Math.min(MAX_QUERY_COUNT, Math.max(MIN_QUERY_COUNT, truncatedCount));
};

const dedupeCaseInsensitivePreserveFirst = (queries: string[]): string[] => {
  const seen = new Set<string>();
  const deduped: string[] = [];

  for (const query of queries) {
    const normalized = query.toLocaleLowerCase();
    if (seen.has(normalized)) {
      continue;
    }

    seen.add(normalized);
    deduped.push(query);
  }

  return deduped;
};

const buildAudienceSuffix = (audience?: string): string => {
  const trimmedAudience = audience?.trim();
  if (!trimmedAudience) {
    return "";
  }

  return ` for ${trimmedAudience}`;
};

const BRAND_VISIBILITY_QUERY_TEMPLATES: ((topic: string, audienceSuffix: string) => string)[] = [
  (topic, audienceSuffix) => `What are the best ${topic} tools${audienceSuffix}?`,
  (topic, audienceSuffix) => `Top ${topic} platforms${audienceSuffix}`,
  (topic, audienceSuffix) => `Which ${topic} solution should I use${audienceSuffix}?`,
  (topic, audienceSuffix) => `Best ${topic} software${audienceSuffix}`,
  (topic, audienceSuffix) => `${topic} alternatives and competitors${audienceSuffix}`,
  (topic, audienceSuffix) => `How do I choose a ${topic} platform${audienceSuffix}?`,
  (topic, audienceSuffix) => `What companies are leaders in ${topic}${audienceSuffix}?`,
  (topic, audienceSuffix) => `Compare the top ${topic} vendors${audienceSuffix}`,
  (topic, audienceSuffix) => `${topic} tools with transparent pricing${audienceSuffix}`,
  (topic, audienceSuffix) => `${topic} tools for small teams${audienceSuffix}`,
  (topic, audienceSuffix) => `${topic} software for enterprise teams${audienceSuffix}`,
  (topic, audienceSuffix) => `${topic} software for startups${audienceSuffix}`,
  (topic, audienceSuffix) => `${topic} platform buying guide${audienceSuffix}`,
  (topic, audienceSuffix) => `How to evaluate ${topic} vendors${audienceSuffix}`,
  (topic, audienceSuffix) => `${topic} software comparison checklist${audienceSuffix}`,
  (topic, audienceSuffix) => `Most popular ${topic} solutions${audienceSuffix}`,
  (topic, audienceSuffix) => `${topic} providers with strong support${audienceSuffix}`,
  (topic, audienceSuffix) => `${topic} tools with good documentation${audienceSuffix}`,
  (topic, audienceSuffix) => `${topic} tools with the best integrations${audienceSuffix}`,
  (topic, audienceSuffix) => `${topic} tool recommendations${audienceSuffix}`,
  (topic, audienceSuffix) => `${topic} options for regulated industries${audienceSuffix}`,
  (topic, audienceSuffix) => `${topic} software for global teams${audienceSuffix}`,
  (topic, audienceSuffix) => `${topic} tools with fast onboarding${audienceSuffix}`,
  (topic, audienceSuffix) => `${topic} tools for agencies${audienceSuffix}`,
  (topic, audienceSuffix) => `${topic} tools for product teams${audienceSuffix}`,
];

export const buildBrandVisibilityStarterQueries = ({
  topic,
  audience,
  count,
}: BuildBrandVisibilityQueriesOptions): string[] => {
  const trimmedTopic = topic.trim();
  if (!trimmedTopic) {
    return [];
  }

  const normalizedCount = normalizeCount(count);
  const audienceSuffix = buildAudienceSuffix(audience);

  const generatedQueries = BRAND_VISIBILITY_QUERY_TEMPLATES.map(buildQuery =>
    buildQuery(trimmedTopic, audienceSuffix),
  );

  return dedupeCaseInsensitivePreserveFirst(generatedQueries).slice(0, normalizedCount);
};

export const parseBrandVisibilityQueries = (input: string): string[] => {
  const lines = input
    .split("\n")
    .map(line => line.trim())
    .filter(Boolean);

  return dedupeCaseInsensitivePreserveFirst(lines);
};
